import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Button as FPButton } from '@finpay-development/shared-components';
import {
    Box,
    Grid,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Checkbox,
    Button
} from '@mui/material';
import { VOBSelector } from './sections/VOBSelector';
import { VOBFormFields } from './sections/VOBFormFields';
import { VOBFormData } from './types';
import { LevelsOfCare } from './sections/LevelsOfCare';
import { NetworkTypeSelector } from './sections/NetworkTypeSelector';
import { AvailityErrorDialog } from './sections/AvailityErrorDialog';
import { DatePicker } from '@finpay-development/shared-components';
import { useVOBForm } from './hooks/useVOBForm';
import { showErrorStatus, showStatus } from '../../../../security/state/user-slice';
import MobilePaymentsLoader from '../../../../shared/components/mobile-payments-loader';
import { downloadVobPdf } from '../../../../admissions-advisor/state/vob-thunk';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { AppDispatch } from '../../../../shared/state/store';
import AccessControl from '../../../../security/components/access-control';
import { RolePageNames } from '../../../../security/model/role-page-names';
import { clearSelectedEstimate } from '../../../../shared/state/estimator-slice';
import { RootState } from 'src/shared/state/root-reducer';
import { saveEstimateAfterVob } from '../../../../shared/state/estimator-thunk';
import { loadAndSetRateAsPayorPlan } from '../../../../estimates/utils/payor-plan-utils';
import { Client, Estimate, Payor, Plan, Vob } from '@finpay/estimation-types';
import { Utils } from '../../../../shared/utils';

function VOBForm() {
    const dispatch = useDispatch<AppDispatch>();
    const [isSaving, setIsSaving] = React.useState(false);
    const [resetButtonStates, setResetButtonStates] = React.useState(false);
    const [buttonStates, setButtonStates] = React.useState<{
        isDeductibleButtonPressed: boolean;
        isOopmButtonPressed: boolean;
        isServiceLimitsButtonPressed: boolean;
        isMaxDaysButtonPressed: boolean;
        isCoinsuranceButtonPressed: boolean;
    } | undefined>(undefined);
    const [dateErrors, setDateErrors] = React.useState({
        effectiveFrom: false,
        effectiveTo: false
    });
    const [facilityError, setFacilityError] = React.useState(false);
    const [deductibleError, setDeductibleError] = React.useState(false);
    const [remainingError, setRemainingError] = React.useState(false);
    const [oopmError, setOopmError] = React.useState(false);
    const [oopmRemainingError, setOopmRemainingError] = React.useState(false);
    const [deductibleErrorMessage, setDeductibleErrorMessage] = React.useState('');
    const [remainingErrorMessage, setRemainingErrorMessage] = React.useState('');
    const [oopmErrorMessage, setOopmErrorMessage] = React.useState('');
    const [oopmRemainingErrorMessage, setOopmRemainingErrorMessage] = React.useState('');
    const [copayError, setCopayError] = React.useState(false);
    const [maxDaysError, setMaxDaysError] = React.useState(false);
    const [coinsuranceError, setCoinsuranceError] = React.useState(false);
    const [copayErrorMessage, setCopayErrorMessage] = React.useState('');
    const [maxDaysErrorMessage, setMaxDaysErrorMessage] = React.useState('');
    const [coinsuranceErrorMessage, setCoinsuranceErrorMessage] = React.useState('');

    const [levelErrors, setLevelErrors] = React.useState<Map<number, {
        deductibleTotalError?: boolean;
        deductibleRemainingError?: boolean;
        oopmTotalError?: boolean;
        oopmRemainingError?: boolean;
        copayError?: boolean;
        maxDaysError?: boolean;
        coinsuranceError?: boolean;
        deductibleTotalErrorMessage?: string;
        deductibleRemainingErrorMessage?: string;
        oopmTotalErrorMessage?: string;
        oopmRemainingErrorMessage?: string;
        copayErrorMessage?: string;
        maxDaysErrorMessage?: string;
        coinsuranceErrorMessage?: string;
    }>>(new Map());

    const levelsOfCare = useSelector(
        (state: RootState) => state.estimatorContext.facilityLevelsOfCareList
    );


    const estimate = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate
    );
    const isSelfPay = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.isSelfPay || false
    );
    const isPrivatePay = useSelector(
        (state: RootState) => state.estimatorContext.selectedEstimate?.isPrivatePay || false
    );
    const reduxRateAsPayorMap = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorMap
    ) || {};
    const reduxMasterPayors = useSelector(
        (state: RootState) => state.adminContext.adminConfigurationContext?.payersMap
    ) || new Map();
    const {
        // States
        activePolicy,
        carryover,
        selectedVOB,
        memberId,
        facilities,
        selectedFacility,
        facilitiesLoading,
        networkType,
        memberType,
        liveVOB,
        formData,
        currentVob,
        isRunningCRMVOB,
        isRunningAvailityVOB,
        isLoadingRunAvailityVOBButton,
        effectiveFromDate,
        effectiveToDate,
        facilityPayersState,
        rateAsPayorPlan,
        isPayerMatched,
        comparisonError,
        selectedPatient,
        selectedPatientCurrentInsurance,
        payersMap,
        estimateId,
        savedLOCDataMap,
        savedFormDataMap,
        showAvailityErrorDialog,
        availityErrorMessage,

        // Handlers
        handleInputChange,
        handleVOBSelectChange,
        handleNetworkTypeChange,
        handleMemberTypeChange,
        handleEffectiveFromDateChange,
        handleEffectiveToDateChange,
        handleFacilityChange,
        setActivePolicy,
        setCarryover,
        setLiveVOB,
        runCRMVob,
        runAvailityVob,
        runAvailityVobWithOverrides,
        handleSaveVOB: saveVOB,
        setSavedFormDataMap,
        setSavedLOCDataMap,
        setShowAvailityErrorDialog,
    } = useVOBForm(() => setResetButtonStates(true), setButtonStates);

    const navigate = useNavigate();

    React.useEffect(() => {
        if (resetButtonStates) {
            // Reset the flag after the next render cycle
            const timer = setTimeout(() => {
                setResetButtonStates(false);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [resetButtonStates]);

    const handleSaveVOB = async () => {
        setIsSaving(true);
        try {
            let hasError = false;
            let errorMessage = 'Please resolve the following issues: ';
            const newDateErrors = {
                effectiveFrom: false,
                effectiveTo: false
            };

            if (!selectedFacility) {
                hasError = true;
                setFacilityError(true);
                errorMessage += 'Facility is required; ';
            } else {
                setFacilityError(false);
            }

            if (!effectiveFromDate) {
                hasError = true;
                newDateErrors.effectiveFrom = true;
                errorMessage += 'Effective From is required; ';
            }

            if (!effectiveToDate) {
                hasError = true;
                newDateErrors.effectiveTo = true;
                errorMessage += 'Effective To is required; ';
            }

            const deductibleTotal = parseFloat(formData.deductible);
            const deductibleRemaining = parseFloat(formData.remaining);
            const oopmTotal = parseFloat(formData.oopm);
            const oopmRemaining = parseFloat(formData.oopmRemaining);
            const copay = parseFloat(formData.copay);
            const maxDays = parseFloat(formData.maxDays);
            const coinsurance = parseFloat(formData.coinsurance);

            let hasNegative = false;
            let hasValidationError = false;

            if (!isNaN(deductibleTotal) && formData.deductible !== '' && deductibleTotal < 0) {
                setDeductibleError(true);
                setDeductibleErrorMessage('Total cannot be negative');
                hasNegative = true;
            } else {
                setDeductibleError(false);
                setDeductibleErrorMessage('');
            }
            if (!isNaN(deductibleRemaining) && formData.remaining !== '' && deductibleRemaining < 0) {
                setRemainingError(true);
                setRemainingErrorMessage('Remaining cannot be negative');
                hasNegative = true;
            } else {
                setRemainingError(false);
                setRemainingErrorMessage('');
            }
            if (!isNaN(oopmTotal) && formData.oopm !== '' && oopmTotal < 0) {
                setOopmError(true);
                setOopmErrorMessage('Total cannot be negative');
                hasNegative = true;
            } else {
                setOopmError(false);
                setOopmErrorMessage('');
            }
            if (!isNaN(oopmRemaining) && formData.oopmRemaining !== '' && oopmRemaining < 0) {
                setOopmRemainingError(true);
                setOopmRemainingErrorMessage('Remaining cannot be negative');
                hasNegative = true;
            } else {
                setOopmRemainingError(false);
                setOopmRemainingErrorMessage('');
            }
            if (!isNaN(copay) && formData.copay !== '' && copay < 0) {
                setCopayError(true);
                setCopayErrorMessage('Co-pay cannot be negative');
                hasNegative = true;
            } else {
                setCopayError(false);
                setCopayErrorMessage('');
            }
            if (!isNaN(maxDays) && formData.maxDays !== '' && maxDays < 0) {
                setMaxDaysError(true);
                setMaxDaysErrorMessage('Max Days cannot be negative');
                hasNegative = true;
            } else {
                setMaxDaysError(false);
                setMaxDaysErrorMessage('');
            }
            if (!isNaN(coinsurance) && formData.coinsurance !== '' && coinsurance < 0) {
                setCoinsuranceError(true);
                setCoinsuranceErrorMessage('Coinsurance cannot be negative');
                hasNegative = true;
            } else if (!isNaN(coinsurance) && formData.coinsurance !== '' && coinsurance > 100) {
                setCoinsuranceError(true);
                setCoinsuranceErrorMessage('Patient % cannot be greater than 100');
                hasValidationError = true;
            } else {
                setCoinsuranceError(false);
                setCoinsuranceErrorMessage('');
            }

            if (hasNegative) {
                setDateErrors(newDateErrors);
                setIsSaving(false);
                dispatch(showErrorStatus('Please correct negative values'));
                return;
            }

            if (hasValidationError) {
                setDateErrors(newDateErrors);
                setIsSaving(false);
                dispatch(showErrorStatus('Please correct validation errors'));
                return;
            }

            if (!isNaN(deductibleTotal) && !isNaN(deductibleRemaining) &&
                formData.deductible !== '' && formData.remaining !== '' &&
                deductibleTotal < deductibleRemaining) {
                hasError = true;
                setDeductibleError(true);
                setRemainingError(true);
                setDeductibleErrorMessage('Deductible Total must be greater than or equal to Remaining');
                setRemainingErrorMessage('Deductible Total must be greater than or equal to Remaining');
                errorMessage += 'Deductible Total must be greater than or equal to Remaining; ';
            } else {
                setDeductibleErrorMessage('');
                setRemainingErrorMessage('');
            }
            if (!isNaN(oopmTotal) && !isNaN(oopmRemaining) &&
                formData.oopm !== '' && formData.oopmRemaining !== '' &&
                oopmTotal < oopmRemaining) {
                hasError = true;
                setOopmError(true);
                setOopmRemainingError(true);
                setOopmErrorMessage('Out of Pocket Maximum Total must be greater than or equal to Remaining');
                setOopmRemainingErrorMessage('Out of Pocket Maximum Total must be greater than or equal to Remaining');
                errorMessage += 'Out of Pocket Maximum Total must be greater than or equal to Remaining; ';
            } else {
                setOopmErrorMessage('');
                setOopmRemainingErrorMessage('');
            }

            if (!hasError) {
                if (formData.deductible && !formData.remaining) {
                    hasError = true;
                    setDeductibleError(false);
                    setRemainingError(true);
                    setRemainingErrorMessage('Remaining is required when Deductible is filled');
                    errorMessage += 'Remaining is required when Deductible is filled; ';
                } else if (!formData.deductible && formData.remaining) {
                    hasError = true;
                    setDeductibleError(true);
                    setRemainingError(false);
                    setDeductibleErrorMessage('Deductible is required when Remaining is filled');
                    errorMessage += 'Deductible is required when Remaining is filled; ';
                } else {
                    setDeductibleError(false);
                    setRemainingError(false);
                    setDeductibleErrorMessage('');
                    setRemainingErrorMessage('');
                }
            }
            if (!hasError) {
                if (formData.oopm && !formData.oopmRemaining) {
                    hasError = true;
                    setOopmError(false);
                    setOopmRemainingError(true);
                    setOopmRemainingErrorMessage('OOP Remaining is required when OOPM is filled');
                    errorMessage += 'OOP Remaining is required when OOPM is filled; ';
                } else if (!formData.oopm && formData.oopmRemaining) {
                    hasError = true;
                    setOopmError(true);
                    setOopmRemainingError(false);
                    setOopmErrorMessage('OOPM is required when OOP Remaining is filled');
                    errorMessage += 'OOPM is required when OOP Remaining is filled; ';
                } else {
                    setOopmError(false);
                    setOopmRemainingError(false);
                    setOopmErrorMessage('');
                    setOopmRemainingErrorMessage('');
                }
            }

            const newLevelErrors = new Map();
            let hasLevelErrors = false;

            if (levelsOfCare && levelsOfCare.length > 0) {
                levelsOfCare.forEach(level => {
                    const vobClassificationId = level.vobClassificationId;
                    if (!vobClassificationId) return;

                    const combinationKey = `${networkType}_${memberType}`;
                    const levelData = savedLOCDataMap.get(vobClassificationId);
                    const savedData = levelData?.get(combinationKey) || {};

                    const levelError: any = {};

                    const deductibleTotal = parseFloat(savedData.total || '');
                    const deductibleRemaining = parseFloat(savedData.remaining || '');

                    if (!isNaN(deductibleTotal) && savedData.total !== '' && deductibleTotal < 0) {
                        levelError.deductibleTotalError = true;
                        levelError.deductibleTotalErrorMessage = 'Total cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(deductibleRemaining) && savedData.remaining !== '' && deductibleRemaining < 0) {
                        levelError.deductibleRemainingError = true;
                        levelError.deductibleRemainingErrorMessage = 'Remaining cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(deductibleTotal) && !isNaN(deductibleRemaining) &&
                        savedData.total !== '' && savedData.remaining !== '' &&
                        deductibleTotal < deductibleRemaining) {
                        levelError.deductibleTotalError = true;
                        levelError.deductibleRemainingError = true;
                        levelError.deductibleTotalErrorMessage = 'Deductible Total must be greater than or equal to Remaining';
                        levelError.deductibleRemainingErrorMessage = 'Deductible Total must be greater than or equal to Remaining';
                        hasLevelErrors = true;
                    }
                    if (savedData.total && !savedData.remaining) {
                        levelError.deductibleRemainingError = true;
                        levelError.deductibleRemainingErrorMessage = 'Remaining is required when Deductible is filled';
                        hasLevelErrors = true;
                    } else if (!savedData.total && savedData.remaining) {
                        levelError.deductibleTotalError = true;
                        levelError.deductibleTotalErrorMessage = 'Deductible is required when Remaining is filled';
                        hasLevelErrors = true;
                    }

                    const oopmTotal = parseFloat(savedData.oopmTotal || '');
                    const oopmRemaining = parseFloat(savedData.oopmRemaining || '');

                    if (!isNaN(oopmTotal) && savedData.oopmTotal !== '' && oopmTotal < 0) {
                        levelError.oopmTotalError = true;
                        levelError.oopmTotalErrorMessage = 'Total cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(oopmRemaining) && savedData.oopmRemaining !== '' && oopmRemaining < 0) {
                        levelError.oopmRemainingError = true;
                        levelError.oopmRemainingErrorMessage = 'Remaining cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(oopmTotal) && !isNaN(oopmRemaining) &&
                        savedData.oopmTotal !== '' && savedData.oopmRemaining !== '' &&
                        oopmTotal < oopmRemaining) {
                        levelError.oopmTotalError = true;
                        levelError.oopmRemainingError = true;
                        levelError.oopmTotalErrorMessage = 'Out of Pocket Maximum Total must be greater than or equal to Remaining';
                        levelError.oopmRemainingErrorMessage = 'Out of Pocket Maximum Total must be greater than or equal to Remaining';
                        hasLevelErrors = true;
                    }
                    if (savedData.oopmTotal && !savedData.oopmRemaining) {
                        levelError.oopmRemainingError = true;
                        levelError.oopmRemainingErrorMessage = 'OOP Remaining is required when OOPM is filled';
                        hasLevelErrors = true;
                    } else if (!savedData.oopmTotal && savedData.oopmRemaining) {
                        levelError.oopmTotalError = true;
                        levelError.oopmTotalErrorMessage = 'OOPM is required when OOP Remaining is filled';
                        hasLevelErrors = true;
                    }

                    const copay = parseFloat(savedData.perDayVisit || '');
                    const maxDays = parseFloat(savedData.maxDays || '');

                    if (!isNaN(copay) && savedData.perDayVisit !== '' && copay < 0) {
                        levelError.copayError = true;
                        levelError.copayErrorMessage = 'Co-pay cannot be negative';
                        hasLevelErrors = true;
                    }
                    if (!isNaN(maxDays) && savedData.maxDays !== '' && maxDays < 0) {
                        levelError.maxDaysError = true;
                        levelError.maxDaysErrorMessage = 'Max Days cannot be negative';
                        hasLevelErrors = true;
                    }


                    const coinsurance = parseFloat(savedData.patient || '');

                    if (!isNaN(coinsurance) && savedData.patient !== '' && coinsurance < 0) {
                        levelError.coinsuranceError = true;
                        levelError.coinsuranceErrorMessage = 'Coinsurance cannot be negative';
                        hasLevelErrors = true;
                    }

                    if (!isNaN(coinsurance) && savedData.patient !== '' && coinsurance > 100) {
                        levelError.coinsuranceError = true;
                        levelError.coinsuranceErrorMessage = 'Patient % cannot be greater than 100';
                        hasLevelErrors = true;
                    }

                    if (Object.keys(levelError).length > 0) {
                        newLevelErrors.set(vobClassificationId, levelError);
                    }
                });
            }

            setLevelErrors(newLevelErrors);
            setDateErrors(newDateErrors);

            if (hasError || hasLevelErrors) {
                const finalErrorMessage = hasLevelErrors ?
                    `${errorMessage} Please check Levels of Care for validation errors.` :
                    errorMessage;
                dispatch(showErrorStatus(finalErrorMessage));
                setIsSaving(false);
                return;
            }

            try {
                const saveSuccess: any = await saveVOB();
                if (saveSuccess) {
                    dispatch(showStatus('VOB saved successfully'));
                    if (estimate && estimate.estimateId) {
                        try {
                            const newVobId = currentVob?.vobId!;
                            const changingFromFacility = (currentVob?.facility.facilityId !== selectedFacility);
                            // if we had changes to the facility or payor we need to run loadandset
                            if(changingFromFacility) {
                                const clientId = Number(selectedPatient.clientId);
                                const clientObj = { clientId } as Client;
                                const currentFacility = { facilityId: Number(selectedFacility) };
                                let updatedEstimate: Estimate = {
                                    ...Utils.deepClone(estimate),
                                    facilityId: selectedFacility,
                                };
                                const selectedPayor: Payor = {
                                    payorId: currentVob?.payor?.payorId
                                        ?? selectedPatientCurrentInsurance?.payorId
                                };
                                const selectedPlan: Plan = {
                                    payorPlanId: currentVob?.plan?.payorPlanId
                                        ?? selectedPatientCurrentInsurance?.payorPlanId
                                };
                                await loadAndSetRateAsPayorPlan(
                                    clientObj,
                                    currentFacility,
                                    dispatch,
                                    reduxMasterPayors,
                                    selectedPayor,
                                    selectedPlan,
                                    reduxRateAsPayorMap,
                                    estimate!,
                                    rateAsPayorPlan,
                                    currentVob!,
                                    updatedEstimate!,
                                    newVobId!
                                );
                            } else {
                              // NO facility / Payor changes, only PUT estimate needed
                                const putEstimateBody = {
                                    ...estimate,
                                    patientId: selectedPatient.patientId,
                                    vobId: newVobId,
                                    ...(rateAsPayorPlan && { rateAsPayorPlan: rateAsPayorPlan }),
                                    isSelfPay: isSelfPay,
                                    isPrivatePay: isPrivatePay,
                                    description: estimate?.description || ''
                                };
                                await dispatch(saveEstimateAfterVob({
                                    estimateId: estimate?.estimateId,
                                    estimate: putEstimateBody,
                                    isSelfPay: isSelfPay,
                                    isPrivatePay: isPrivatePay
                                    }));
                                }

                            dispatch(showStatus('VOB and Estimate saved successfully'));
                        } catch (estimateError) {
                            console.error('Error saving estimate after VOB:', estimateError);
                            dispatch(showStatus('VOB saved successfully, but estimate save failed'));
                        }
                    }
                }
            } catch (error) {
                dispatch(showStatus('VOB saved failed'));
            }
        } finally {
            setIsSaving(false);
        }
    };


    const handleDownloadPdf = () => {
        if (currentVob?.vobId) {
            dispatch(downloadVobPdf(currentVob.vobId));
        }
    };

    return (
        <Box test-id="vob-form-container">
            <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Grid item xs sx={{ minWidth: 0 }}>
                    <VOBSelector
                        selectedVOB={selectedVOB}
                        onVOBChange={handleVOBSelectChange}
                        rateAsPayorPlan={rateAsPayorPlan}
                        facilities={facilities}
                    />
                </Grid>
                <Grid item sx={{
                    minWidth: '200px',
                    flexShrink: 0,
                    flexGrow: 0,
                    width: 'auto',
                    paddingTop: "0px !important"
                }}>
                    <Box test-id="vob-form-actions" sx={{
                        display: 'flex',
                        gap: 1,
                        flexWrap: 'nowrap',
                        minWidth: 'fit-content',
                        width: 'fit-content',
                    }}>
                        <FPButton
                            test-id="vob-new-button"
                            onClick={async () => {
                                if (selectedPatient?.patientId) {
                                    const now = new Date();
                                    const year = now.getFullYear();
                                    setActivePolicy(true);
                                    setCarryover(false);
                                    setLiveVOB(false);
                                    handleEffectiveFromDateChange(new Date(`${year}-01-01`));
                                    handleEffectiveToDateChange(new Date(`${year}-12-31`));
                                    handleInputChange('deductible', '');
                                    handleInputChange('remaining', '');
                                    handleInputChange('oopm', '');
                                    handleInputChange('oopmRemaining', '');
                                    handleInputChange('copay', '');
                                    handleInputChange('maxDays', '');
                                    handleInputChange('coinsurance', '');
                                    handleInputChange('notes', '');
                                    handleInputChange('isDedIncOOPM', true);
                                    handleVOBSelectChange('');
                                    setResetButtonStates(true);
                                    setSavedFormDataMap(new Map());
                                    setSavedLOCDataMap(new Map());
                                    dispatch(clearSelectedEstimate());
                                    navigate(`/patients/${selectedPatient.patientId}/vob`);
                                    if (selectedFacility && selectedPatient?.clientId) {
                                        const client = { clientId: Number(selectedPatient.clientId) } as Client;
                                        const facility = { facilityId: Number(selectedFacility) };
                                        const payor = {
                                            payorId: selectedPatientCurrentInsurance.payorId
                                        } as Payor;
                                        const plan = {
                                            payorPlanId: selectedPatientCurrentInsurance.payorPlanId
                                        } as Plan;
                                        await loadAndSetRateAsPayorPlan(
                                            client,
                                            facility,
                                            dispatch,
                                            reduxMasterPayors,
                                            payor,
                                            plan,
                                            reduxRateAsPayorMap
                                        );
                                    }
                                }
                            }}
                            style={{
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                        >
                            New
                        </FPButton>
                        <FPButton
                            test-id="vob-save-button"
                            onClick={handleSaveVOB}
                            disabled={isSaving}
                            style={{
                                minWidth: 'fit-content',
                                whiteSpace: 'nowrap',
                                flexShrink: 0
                            }}
                            // startIcon={isSaving ? <CircularProgress size={20} /> : null}
                        >
                            {isSaving ? 'Saving...' : 'Save'}
                        </FPButton>
                    </Box>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="facility-select-label">
                            {facilitiesLoading || (!selectedFacility && facilities.length === 0) ? 'Loading...' : 'Facility'}
                        </InputLabel>
                        <Select
                            test-id="vob-facility-select"
                            labelId="facility-select-label"
                            id="facility-select"
                            value={selectedFacility}
                            label={facilitiesLoading || (!selectedFacility && facilities.length === 0) ? 'Loading...' : 'Facility'}
                            onChange={(e) => handleFacilityChange(Number(e.target.value))}
                            error={facilityError}
                            disabled={facilitiesLoading || (!selectedFacility && facilities.length === 0)}
                            className={`state-field ${facilityError ? 'error-border' : 'required-fields'}`}
                        >
                            {facilitiesLoading || (!selectedFacility && facilities.length === 0) ? (
                                <MenuItem disabled>Loading facilities...</MenuItem>
                            ) : (
                                facilities.map((facility) => (
                                    <MenuItem key={facility.facilityId} value={facility.facilityId}>
                                        {facility.facilityName}
                                    </MenuItem>
                                ))
                            )}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography test-id="vob-payer-display" variant="body1" sx={{ mr: 2 }}>
                        Payer: {
                            currentVob?.payor?.payorId ?
                            `${payersMap?.get(currentVob.payor.payorId)?.payorName || currentVob.payor.payorName}${
                                currentVob.plan?.payorPlanId && 
                                payersMap?.get(currentVob.payor.payorId)?.payorPlans?.find(
                                    plan => Number(plan.payorPlanId) === Number(currentVob.plan?.payorPlanId)
                                )?.planName ? 
                                `/${payersMap.get(currentVob.payor.payorId)?.payorPlans?.find(
                                    plan => Number(plan.payorPlanId) === Number(currentVob.plan?.payorPlanId)
                                )?.planName}` : 
                                currentVob.plan?.planName ? `/${currentVob.plan.planName}` : ''
                            }` :
                            selectedPatientCurrentInsurance?.payorId && payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorName ?
                            `${payersMap.get(selectedPatientCurrentInsurance.payorId)?.payorName}${
                                selectedPatientCurrentInsurance?.payorId &&
                                selectedPatientCurrentInsurance?.payorPlanId &&
                                payersMap?.get(selectedPatientCurrentInsurance.payorId)?.payorPlans?.find(
                                    plan => Number(plan.payorPlanId) === selectedPatientCurrentInsurance.payorPlanId
                                )?.planName ? 
                                `/${payersMap.get(selectedPatientCurrentInsurance.payorId)?.payorPlans?.find(
                                    plan => Number(plan.payorPlanId) === selectedPatientCurrentInsurance.payorPlanId
                                )?.planName}` : ''
                            }` : ''
                        }
                    </Typography>
                    {comparisonError && (
                        <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                            ({comparisonError})
                        </Typography>
                    )}
                </Grid>
                <Grid item xs={4} sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography test-id="vob-member-id-display" variant="body1">
                        Member ID: {memberId}
                    </Typography>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={4}>
                    {isLoadingRunAvailityVOBButton ? (
                        <Button
                            onClick={runAvailityVob}
                            disabled={isRunningAvailityVOB}
                            test-id="run-availability-vob"
                        >
                            Run Availity VOB
                        </Button>
                    ) : null}
                </Grid>
                <Grid item xs={8} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        test-id="vob-live-vob-checkbox"
                        control={
                            <Checkbox
                                test-id="vob-live-vob-checkbox-input"
                                checked={liveVOB}
                                onChange={(e) => setLiveVOB(e.target.checked)}
                            />
                        }
                        label="Live VOB"
                    />
                </Grid>
            </Grid>

            <MobilePaymentsLoader
                test-id="vob-availity-loader"
                showLoader={isRunningAvailityVOB}
                customMessage="Running"
            />

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={3}>
                    <DatePicker
                        test-id="vob-effective-from-date"
                        label="Effective From"
                        value={effectiveFromDate}
                        onChange={handleEffectiveFromDateChange}
                        error={dateErrors.effectiveFrom}
                        className={`state-field ${dateErrors.effectiveFrom ? 'error-border' : 'required-fields'}`}
                    />
                </Grid>
                <Grid item xs={3}>
                    <DatePicker
                        test-id="vob-effective-to-date"
                        label="Effective To"
                        value={effectiveToDate}
                        onChange={handleEffectiveToDateChange}
                        error={dateErrors.effectiveTo}
                        className={`state-field ${dateErrors.effectiveTo ? 'error-border' : 'required-fields'}`}
                    />
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel
                        test-id="vob-active-policy-checkbox"
                        control={
                            <Checkbox
                                test-id="vob-active-policy-checkbox-input"
                                checked={activePolicy}
                                onChange={(e) => setActivePolicy(e.target.checked)}
                            />
                        }
                        label="Active Policy"
                    />
                    <FormControlLabel
                        test-id="vob-carryover-checkbox"
                        control={
                            <Checkbox
                                test-id="vob-carryover-checkbox-input"
                                checked={carryover}
                                onChange={(e) => setCarryover(e.target.checked)}
                            />
                        }
                        label="4th Quarter Carryover"
                    />
                </Grid>
            </Grid>

            <NetworkTypeSelector
                networkType={networkType}
                memberType={memberType}
                onNetworkTypeChange={handleNetworkTypeChange}
                onMemberTypeChange={handleMemberTypeChange}
            />

            <VOBFormFields
                formData={formData}
                onInputChange={handleInputChange}
                currentVob={currentVob}
                deductibleError={deductibleError}
                remainingError={remainingError}
                oopmError={oopmError}
                oopmRemainingError={oopmRemainingError}
                deductibleErrorMessage={deductibleErrorMessage}
                remainingErrorMessage={remainingErrorMessage}
                oopmErrorMessage={oopmErrorMessage}
                oopmRemainingErrorMessage={oopmRemainingErrorMessage}
                copayError={copayError}
                maxDaysError={maxDaysError}
                coinsuranceError={coinsuranceError}
                copayErrorMessage={copayErrorMessage}
                maxDaysErrorMessage={maxDaysErrorMessage}
                coinsuranceErrorMessage={coinsuranceErrorMessage}
                resetButtonStates={resetButtonStates}
                setButtonStates={buttonStates}
            />

            <LevelsOfCare
                levels={levelsOfCare}
                rateAsPayorPlan={rateAsPayorPlan}
                networkType={networkType}
                memberType={memberType}
                setSavedLOCDataMap={setSavedLOCDataMap}
                savedLOCDataMap={savedLOCDataMap}
                savedFormDataMap={savedFormDataMap}
                levelErrors={levelErrors}
            />

            <Box test-id="vob-form-bottom-actions" sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <AccessControl
                    rolePageName={RolePageNames.Estimates}
                    actionName="PDF Download"
                    renderNoAccess={() => null}
                >
                    <Button
                        test-id="vob-download-pdf-button"
                        variant="contained"
                        color="primary"
                        style={{marginRight: "6px"}}
                        onClick={handleDownloadPdf}
                        disabled={!currentVob?.vobId || isSaving}
                        startIcon={<PictureAsPdfIcon />}
                    >
                        Download PDF
                    </Button>
                </AccessControl>
                <FPButton
                    test-id="vob-save-button-bottom"
                    onClick={handleSaveVOB}
                    disabled={isSaving}
                    // startIcon={isSaving ? <CircularProgress size={20} /> : null}
                >
                    {isSaving ? 'Saving...' : 'Save'}
                </FPButton>
            </Box>

            <AvailityErrorDialog
                open={showAvailityErrorDialog}
                onClose={() => setShowAvailityErrorDialog(false)}
                availityErrorMessage={availityErrorMessage}
                selectedPatient={selectedPatient}
                selectedPatientCurrentInsurance={selectedPatientCurrentInsurance}
                isRunningAvailityVOB={isRunningAvailityVOB}
                onRunAvailityVob={runAvailityVobWithOverrides}
            />
        </Box>
    );
}

export default VOBForm;